import { LoaderComponent } from 'src/app/components/widgets/loader/loader.component';
import { Component, EventEmitter, Input, OnInit, Output, ViewChild, AfterViewInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { InputFieldComponent } from '../../../../components/form/input-field/input-field.component';
import { ConfirmationDialogService } from '../../../../services/confirmation-dialog.service';
import {  NgbModal, NgbModalModule } from '@ng-bootstrap/ng-bootstrap';
import { AlertNotificationService } from 'src/app/services/alert-notification.service';
import { ProjectsService } from 'src/app/services/api/projects.service';
import { catchError, finalize, switchMap } from 'rxjs/operators';
import { of, EMPTY, Observable, firstValueFrom } from 'rxjs';
import { EmployeeConsultantsService } from 'src/app/services/api/hr/employee-consultants.service';
import { MainLayoutService } from 'src/app/services/main-layout.service';
import { AccessControlService } from 'src/app/services/access-control.service';

interface AvailableStaff {
  employee: Employee
  actitude: string
  workPercent: number
  workingProjects: WorkingProject[]
  projectTeamId?: number
}

 interface Employee {
  employeeId: number
  workingCompany: WorkingCompany
  firstName: string
  lastName: string
  birthdate: string
  addressLine1: string
  addressLine2: string
  zipCode: string
  city: string
  countryId: number
  code: string
  engageTime: string
  role: string
  actitude: string
  contractType: string
  status: string
  employeeSpecialisations: any[]
}

 interface WorkingCompany {
  companyId: number
  code: string
  name: string
  country: Country
}

 interface Country {
  countryId: number
  name: string
  iso2: string
  iso3: string
  code: number
}

interface WorkingProject {
  projectId: number
  code: string
  statusProgress: number
  clientCompanyName: string
}

@Component({
  selector: 'projects-team',
  imports: [
    CommonModule, 
    ReactiveFormsModule, 
    InputFieldComponent, 
    NgbModalModule,
    LoaderComponent
  ],
  templateUrl: './projects-team.component.html',
  styleUrl: './projects-team.component.scss'
})
export class ProjectsTeamComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('personDetailModal') personDetailModal: any;
  @Input() projectId?: string;
  loading: boolean = false;
  @Output() onTeamUpdated = new EventEmitter<any>();
  
  hasOtherMembers: boolean = false;
  isCustomerProfile: boolean = false;

  form: FormGroup;
  teamLeaderForm: FormGroup;
  teamMembersArray: FormArray;
  
  teamConflictForm: FormGroup;
  teamConflictLoading: boolean = false;
  teamConflictEditable: boolean = true;
  teamConflictData: any = null;
  teamConflictCreatorName: string = '';
  teamConflictCreateTime: string = '';
  
  availableStaff: AvailableStaff[] = [];
  
  get filteredAvailableStaff(): AvailableStaff[] {
    if (this.showAllAvailableStaff) {
      return this.availableStaff;
    }
    
    return this.availableStaff.filter(staff => {
      const employeeId = staff.employee?.employeeId;
      const fullName = `${staff.employee?.firstName} ${staff.employee?.lastName}`;
      
      if (!employeeId) return true; 
      
      const conflictType = this.getTeamConflictType(employeeId, fullName);
      return conflictType === 'none';
    });
  }

  toggleShowAllStaff(): void {
    this.showAllAvailableStaff = !this.showAllAvailableStaff;
  }

  safeGetTeamConflictType(staff: AvailableStaff): 'team-leader' | 'team-member' | 'none' {
    if (!staff?.employee?.employeeId) {
      return 'none';
    }
    
    const fullName = `${staff.employee.firstName || ''} ${staff.employee.lastName || ''}`.trim();
    return this.getTeamConflictType(staff.employee.employeeId, fullName);
  }
  
  memberToDelete: any = null;
  teamMemberIndexToDelete: number = -1;
  memberToDeleteProjectTeamId?: number;
  isDeleting: boolean = false;  
  selectedPerson?: any;
  selectedPersonIndex?: number;
  selectedMoodIcon: string = 'neutral';
  selectedPersonDetails?: any;
  loadingPersonDetails: boolean = false;
  
  showAllAvailableStaff: boolean = false;

  teamChanged: boolean = false;
  private persistedTeamSignature: string | null = null;
  canSendApproval: boolean = false;
  sendingApproval: boolean = false;
  canViewAvailableStaff: boolean = true;
  canEditAssignment: boolean = true;
  canUseMoodButton: boolean = true;

  constructor(
    private fb: FormBuilder,
    private modalService: NgbModal,
    private alertNotificationService: AlertNotificationService,
    private projectsService: ProjectsService,
    private employeeService: EmployeeConsultantsService,
    private confirmationDialogService: ConfirmationDialogService,
    private mainLayoutService: MainLayoutService,
    private accessControlService: AccessControlService
  ) {
    this.teamLeaderForm = this.fb.group({
      name: [''],
      hours: [''],
      mood: ['neutral'],
      projectTeamId: [null],
      employeeId: [null]
    });
    
    this.teamMembersArray = this.fb.array([]);
    
    this.form = this.fb.group({
      teamLeader: this.teamLeaderForm,
      teamMembers: this.teamMembersArray,
      totalRealHours: [''],
      totalEstimatedHours: ['']
    });

    this.teamConflictForm = this.fb.group({
      hasConflict: [null],
      notes: ['']
    });

    this.setupTeamLeaderValidations();
  }

  private setupTeamLeaderValidations(): void {
    this.teamLeaderForm.get('employeeId')?.valueChanges.subscribe(employeeId => {
      if (employeeId) {
        this.validateTeamLeaderEmployeeId(employeeId);
      }
    });
    
    this.teamLeaderForm.get('name')?.valueChanges.subscribe(name => {
      if (name && name.trim()) {
        this.validateTeamLeaderName(name.trim());
      }
    });
  }

  private validateTeamLeaderEmployeeId(employeeId: number): void {
    let isDuplicateInMembers = false;
    for (let i = 0; i < this.teamMembersArray.length; i++) {
      if (this.teamMembersArray.at(i).get('employeeId')?.value === employeeId) {
        isDuplicateInMembers = true;
        break;
      }
    }
    
    if (isDuplicateInMembers) {
      this.teamLeaderForm.get('employeeId')?.setErrors({ duplicate: true });
    } else {
      const currentErrors = this.teamLeaderForm.get('employeeId')?.errors;
      if (currentErrors?.['duplicate']) {
        delete currentErrors['duplicate'];
        const hasOtherErrors = Object.keys(currentErrors).length > 0;
        this.teamLeaderForm.get('employeeId')?.setErrors(hasOtherErrors ? currentErrors : null);
      }
    }
  }

  private validateTeamLeaderName(name: string): void {
    let isDuplicateInMembers = false;
    for (let i = 0; i < this.teamMembersArray.length; i++) {
      const memberName = this.teamMembersArray.at(i).get('name')?.value;
      if (memberName && memberName.trim().toLowerCase() === name.toLowerCase()) {
        isDuplicateInMembers = true;
        break;
      }
    }
    
    if (isDuplicateInMembers) {
      this.teamLeaderForm.get('name')?.setErrors({ duplicate: true });
    } else {
      const currentErrors = this.teamLeaderForm.get('name')?.errors;
      if (currentErrors?.['duplicate']) {
        delete currentErrors['duplicate'];
        const hasOtherErrors = Object.keys(currentErrors).length > 0;
        this.teamLeaderForm.get('name')?.setErrors(hasOtherErrors ? currentErrors : null);
      }
    }
  }

  ngOnInit(): void {
    this.loadAvailableStaff();
    this.loadTeamData();
    this.loadTeamConflicts();
    this.checkCustomerProfile();
    this.checkCanSendApproval();
    this.checkTeamPanelAccess();
  }

  private checkTeamPanelAccess(): void {
    this.mainLayoutService.activeProfile$.subscribe(profileCode => {
      this.canViewAvailableStaff = this.accessControlService.hasFeatureActionAccess(
        profileCode, 'PROJECTS_TEAM_AVAILABLE_STAFF', 'view'
      );
      this.canEditAssignment = this.accessControlService.hasFeatureActionAccess(
        profileCode, 'PROJECTS_TEAM_ASSIGNMENT_EDIT', 'modify'
      );
      this.canUseMoodButton = this.accessControlService.hasFeatureActionAccess(
        profileCode, 'PROJECTS_TEAM_MOOD_BUTTON', 'modify'
      );
    });
  }

  private checkCustomerProfile(): void {
    this.mainLayoutService.activeProfile$.subscribe(profileCode => {
      const customerProfiles = ['CUS1', 'CUS2', 'CUS3'];
      this.isCustomerProfile = profileCode ? customerProfiles.includes(profileCode) : false;
      this.updateFormEditableState();
    });
  }

  private checkCanSendApproval(): void {
    this.mainLayoutService.activeProfile$.subscribe(profileCode => {
      this.canSendApproval = this.accessControlService.hasFeatureActionAccess(
        profileCode, 'PROJECTS_TEAM_SEND_APPROVAL', 'modify'
      );
    });
  }

  private updateFormEditableState(): void {
    if (this.teamConflictEditable && this.isCustomerProfile) {
      this.teamConflictForm.enable();
    } else {
      this.teamConflictForm.disable();
    }
  }

  private convertRateToMood(rate: string | number): string {
    const rateStr = typeof rate === 'number' ? rate.toString() : rate;
    
    switch (rateStr.toUpperCase()) {
      case 'GREEN':
        return 'happy';
      case 'YELLOW':
        return 'neutral';
      case 'RED':
        return 'sad';
      default:
        return 'neutral';
    }
  }

  private convertMoodToRate(mood: string): string {
    switch (mood.toLowerCase()) {
      case 'happy':
        return 'GREEN';
      case 'neutral':
        return 'YELLOW';
      case 'sad':
        return 'RED';
      default:
        return 'YELLOW';
    }
  }

  private clearTeamData(): void {
    this.teamLeaderForm.patchValue({
      name: '',
      hours: '',
      mood: 'neutral',
      projectTeamId: null,
      employeeId: null
    });
    
    while (this.teamMembersArray.length > 0) {
      this.teamMembersArray.removeAt(0);
    }
    
    this.teamLeaderForm.get('employeeId')?.setErrors(null);
    this.teamLeaderForm.get('name')?.setErrors(null);
    
    this.updateHasOtherMembers();
  }

  loadTeamData(): void {
    this.loading = true;
    
    this.projectsService.listProjectTeams(Number(this.projectId))
      .pipe(
        catchError(error => {
          this.alertNotificationService.error('Error loading available staff: ' + error.message);
          return of({ data: { projectTeams: [] }});
        }),
        finalize(() => this.loading = false)
      ).subscribe({
          next: (response) => {
            this.clearTeamData();

            this.teamChanged = !!response.data.changed;

            const teams = response.data.projectTeams;
            if (teams && teams.length > 0) {
              const teamLeader = teams.find((t: any) => t.role === 'TEAM_LEADER');
              if (teamLeader) {
                const mood = this.convertRateToMood(teamLeader.rate || 'neutral');                
                this.teamLeaderForm.patchValue({
                  name: teamLeader.employee.firstName + ' ' + teamLeader.employee.lastName,
                  hours: teamLeader.projectTime,
                  mood: mood,
                  projectTeamId: teamLeader.projectTeamId,
                  employeeId: teamLeader.employee.employeeId
                });
              }

              const teamMembers = teams.filter((t: any) => t.role !== 'TEAM_LEADER');
              teamMembers.forEach((member: any) => {
                const mood = this.convertRateToMood(member.rate || 'neutral');
                
                this.addTeamMember({
                  name: member.employee.firstName + ' ' + member.employee.lastName,
                  hours: member.projectTime,
                  mood: mood,
                  projectTeamId: member.projectTeamId,
                  employeeId: member.employee.employeeId
                });
              });

              this.calculateTotals();
            }

            this.persistedTeamSignature = this.buildSignatureFromApi(teams);
          }
        });

  }

  private buildTeamSignature(entries: { employeeId: any; role: string; hours: any; rate: string }[]): string {
    return entries
      .map(entry => `${entry.employeeId}|${entry.role}|${Number(entry.hours) || 0}|${entry.rate}`)
      .sort()
      .join(';');
  }

  private buildSignatureFromApi(teams: any[]): string {
    const entries = (teams || [])
      .filter((team: any) => team?.employee?.employeeId)
      .map((team: any) => ({
        employeeId: team.employee.employeeId,
        role: team.role === 'TEAM_LEADER' ? 'TEAM_LEADER' : 'OTHER',
        hours: team.projectTime,
        rate: this.convertMoodToRate(this.convertRateToMood(team.rate || 'neutral'))
      }));

    return this.buildTeamSignature(entries);
  }

  private buildSignatureFromForm(): string {
    const formData = this.form.value;
    const entries: { employeeId: any; role: string; hours: any; rate: string }[] = [];

    if (formData.teamLeader?.name) {
      entries.push({
        employeeId: formData.teamLeader.employeeId,
        role: 'TEAM_LEADER',
        hours: formData.teamLeader.hours,
        rate: this.convertMoodToRate(formData.teamLeader.mood || 'neutral')
      });
    }

    (formData.teamMembers || []).forEach((member: any) => {
      if (member?.name) {
        entries.push({
          employeeId: member.employeeId,
          role: 'OTHER',
          hours: member.hours,
          rate: this.convertMoodToRate(member.mood || 'neutral')
        });
      }
    });

    return this.buildTeamSignature(entries);
  }


  ngAfterViewInit(): void {
  
  }

  loadAvailableStaff(): void {
    if (this.projectId) {
      this.loading = true;
      this.projectsService.availableProjectTeams(Number(this.projectId))
        .pipe(
          catchError(error => {
            this.alertNotificationService.error('Error loading team: ' + error.message);
            return of({ data: { projectTeamAvailableStaffs: [] }});
          }),
          finalize(() => this.loading = false)
        ).subscribe({
        next: (response) => {
          if (response && response.data && response.data.projectTeamAvailableStaffs) {
            this.availableStaff = response.data.projectTeamAvailableStaffs;
          }
        }
      });
    }
  }

  loadTeamConflicts(): void {
    if (this.projectId) {
      this.teamConflictLoading = true;
      this.projectsService.listProjectTeamConfilts(Number(this.projectId))
        .pipe(
          catchError(error => {
            if (error.status === 404) {
              this.teamConflictEditable = true;
              this.updateFormEditableState();
              return of(null);
            }
            this.alertNotificationService.error('Error loading team conflicts: ' + error.message);
            return of(null);
          }),
          finalize(() => this.teamConflictLoading = false)
        ).subscribe({
          next: (response) => {
            const projectTeamConflict = response?.data?.projectTeamConflict;

            if (projectTeamConflict) {
              this.teamConflictEditable = false;
              this.teamConflictData = projectTeamConflict;

              if (projectTeamConflict.creator) {
                this.teamConflictCreatorName = `${projectTeamConflict.creator.lastName || ''} ${projectTeamConflict.creator.firstName || ''}`.trim();
                this.teamConflictCreateTime = projectTeamConflict.createTime || '';
              } else {
                this.teamConflictCreatorName = '';
                this.teamConflictCreateTime = '';
              }

              this.teamConflictForm.patchValue({
                hasConflict: projectTeamConflict.hasConflict,
                notes: projectTeamConflict.notes || ''
              });

              this.teamConflictForm.disable();
            } else {
              this.teamConflictData = null;
              this.teamConflictEditable = true;
              this.updateFormEditableState();
              this.teamConflictCreatorName = '';
              this.teamConflictCreateTime = '';
              this.teamConflictForm.patchValue({ hasConflict: null, notes: '' });
            }
          }
        });
    }
  }

  saveTeamConflicts(): void {
    if (!this.projectId) {
      this.alertNotificationService.error('Invalid project ID');
      return;
    }

    const formValue = this.teamConflictForm.getRawValue();
    const payload = {
      hasConflict: formValue.hasConflict,
      notes: formValue.notes || ''
    };

    this.teamConflictLoading = true;
    this.projectsService.updateProjectTeamConfilts(Number(this.projectId), payload)
      .pipe(
        catchError(error => {
          this.alertNotificationService.error('Error saving team conflicts: ' + error.message);
          return of(null);
        }),
        finalize(() => this.teamConflictLoading = false)
      )
      .subscribe({
        next: (response) => {
          if (response && response.data) {
            this.alertNotificationService.success('Team conflicts saved successfully');
            this.teamConflictData = response.data;
            
            if (response.data.createTime && response.data.creator) {
              this.teamConflictCreateTime = response.data.createTime;
              this.teamConflictCreatorName = `${response.data.creator.lastName || ''} ${response.data.creator.firstName || ''}`.trim();
            }
            
            this.teamConflictEditable = false;
            this.teamConflictForm.disable();
          }
        }
      });
  }

  onConflictTypeChange(hasConflict: boolean): void {
    this.teamConflictForm.patchValue({ hasConflict });
    if (!hasConflict) {
      this.teamConflictForm.patchValue({ notes: '' });
    }
  }

  sendApprovalToClient(): void {
    if (!this.projectId) {
      this.alertNotificationService.error('Invalid project ID');
      return;
    }

    this.sendingApproval = true;
    this.projectsService.sendProjectTeamApproval(Number(this.projectId))
      .pipe(
        catchError(error => {
          this.alertNotificationService.error('Error sending approval to client: ' + error.message);
          return of(null);
        }),
        finalize(() => this.sendingApproval = false)
      )
      .subscribe(response => {
        if (response) {
          this.alertNotificationService.success('Approval sent to client successfully');
          this.loadTeamData();
        }
      });
  }

  get teamMembers(): FormArray {
    return this.form.get('teamMembers') as FormArray;
  }

  addTeamMember(data: any = null): void {
    if (data?.employeeId && data?.name) {
      const conflictType = this.getTeamConflictType(data.employeeId, data.name);
      if (conflictType !== 'none') {
        let message = '';
        if (conflictType === 'team-leader') {
          message = `${data.name} is already set as Team Leader.`;
        } else if (conflictType === 'team-member') {
          message = `${data.name} is already present among Other Team Members.`;
        }
        this.alertNotificationService.warning(message);
        return;
      }
    }
    
    const memberForm = this.fb.group({
      name: [data ? data.name : ''],
      hours: [data ? data.hours : ''],
      mood: [data ? data.mood : 'neutral'],
      projectTeamId: [data ? data.projectTeamId : null],
      employeeId: [data ? data.employeeId : null]
    });
    
    memberForm.get('employeeId')?.valueChanges.subscribe(employeeId => {
      if (employeeId) {
        this.validateMemberEmployeeId(memberForm, employeeId);
      }
    });
    
    memberForm.get('name')?.valueChanges.subscribe(name => {
      if (name && name.trim()) {
        this.validateMemberName(memberForm, name.trim());
      }
    });
    
    this.teamMembersArray.push(memberForm);
    this.updateHasOtherMembers();
  }

  private validateMemberEmployeeId(memberForm: any, employeeId: number): void {
    const currentIndex = this.teamMembersArray.controls.indexOf(memberForm);
    const isLeaderConflict = this.teamLeaderForm.get('employeeId')?.value === employeeId;
    
    let isDuplicateInMembers = false;
    for (let i = 0; i < this.teamMembersArray.length; i++) {
      if (i !== currentIndex && this.teamMembersArray.at(i).get('employeeId')?.value === employeeId) {
        isDuplicateInMembers = true;
        break;
      }
    }
    
    if (isLeaderConflict || isDuplicateInMembers) {
      memberForm.get('employeeId')?.setErrors({ duplicate: true });
    } else {
      const currentErrors = memberForm.get('employeeId')?.errors;
      if (currentErrors?.['duplicate']) {
        delete currentErrors['duplicate'];
        const hasOtherErrors = Object.keys(currentErrors).length > 0;
        memberForm.get('employeeId')?.setErrors(hasOtherErrors ? currentErrors : null);
      }
    }
  }

  private validateMemberName(memberForm: any, name: string): void {
    const currentIndex = this.teamMembersArray.controls.indexOf(memberForm);
    const isLeaderConflict = this.teamLeaderForm.get('name')?.value?.trim().toLowerCase() === name.toLowerCase();
    
    let isDuplicateInMembers = false;
    for (let i = 0; i < this.teamMembersArray.length; i++) {
      if (i !== currentIndex && 
          this.teamMembersArray.at(i).get('name')?.value?.trim().toLowerCase() === name.toLowerCase()) {
        isDuplicateInMembers = true;
        break;
      }
    }
    
    if (isLeaderConflict || isDuplicateInMembers) {
      memberForm.get('name')?.setErrors({ duplicate: true });
    } else {
      const currentErrors = memberForm.get('name')?.errors;
      if (currentErrors?.['duplicate']) {
        delete currentErrors['duplicate'];
        const hasOtherErrors = Object.keys(currentErrors).length > 0;
        memberForm.get('name')?.setErrors(hasOtherErrors ? currentErrors : null);
      }
    }
  }

  removeTeamMember(index: number): void {
    const member = this.teamMembersArray.at(index).value;
    this.teamMembersArray.removeAt(index);
    this.updateHasOtherMembers();
    this.calculateTotals();
    this.loadAvailableStaff(); 
  }

  calculateTotals(): void {
    let totalRealHours = 0;
    let totalEstimatedHours = 0;
    
    const leaderHours = parseInt(this.teamLeaderForm.get('hours')?.value || '0');
    totalRealHours += leaderHours;
    totalEstimatedHours += leaderHours;
    
    this.teamMembers.controls.forEach(member => {
      totalRealHours += parseInt(member.get('hours')?.value || '0');
      totalEstimatedHours += parseInt(member.get('hours')?.value || '0');
    });
    
    this.form.patchValue({
      totalRealHours,
      totalEstimatedHours
    });
  }

  openPersonDetailModal(content: any, person?: any): void {
    this.selectedPerson = person;
    this.loadingPersonDetails = true;
    this.selectedPersonDetails = null;

    const modalRef = this.modalService.open(content, { centered: true, size: 'lg' });
    
    if (person && person.employee && person.employee.employeeId) {
      this.employeeService.detailWorkingProjects(person.employee.employeeId)
        .pipe(
          catchError(error => {
            this.alertNotificationService.error('Error loading staff projects: ' + error.message);
            return of({ data: { workingProjects: [] }});
          }),
          finalize(() => this.loadingPersonDetails = false)
        )
        .subscribe({
          next: (response) => {
            try {
              const workingProjects = response?.data?.workingProjects || [];
              const projects = workingProjects.map((wp: WorkingProject) => {
                const statusProgress = wp.statusProgress ?? 0;
                let status = 'Unknown';
                let statusClass = 'secondary';

                if (statusProgress >= 75) {
                  status = 'High';
                  statusClass = 'success';
                } else if (statusProgress >= 50) {
                  status = 'Medium';
                  statusClass = 'warning';
                } else if (statusProgress >= 25) {
                  status = 'Low';
                  statusClass = 'info';
                } else {
                  status = 'Idle';
                  statusClass = 'secondary';
                }

                return {
                  code: wp.code,
                  company: wp.clientCompanyName,
                  status,
                  statusClass,
                  dueDate: null,
                  statusProgress
                };
              });

              this.selectedPersonDetails = { projects };
              if (projects.length > 0) {
                const total = projects.length;
                const criticalCount = projects.filter((p: any) => p.statusProgress >= 75).length;
                const warningCount = projects.filter((p: any) => p.statusProgress >= 50 && p.statusProgress < 75).length;
                const moderateCount = projects.filter((p: any) => p.statusProgress >= 25 && p.statusProgress < 50).length;
                const normalCount = projects.filter((p: any) => p.statusProgress < 25).length;

                const toPercent = (n: number) => Math.round((n / total) * 100);
                let critical = toPercent(criticalCount);
                let warning = toPercent(warningCount);
                let moderate = toPercent(moderateCount);
                let normal = toPercent(normalCount);

                const sum = critical + warning + moderate + normal;
                if (sum !== 100) {
                  normal += 100 - sum;
                }

                const workload = { critical, warning, moderate, normal }; 
                this.initDonutChart(workload);
              } else {
                this.initDonutChart();
              }
            } catch (err: any) {
              this.alertNotificationService.error('Error preparing person details: ' + (err?.message || err));
              this.selectedPersonDetails = { projects: [] };
              this.initDonutChart();
            }
          },
          error: () => {
            this.selectedPersonDetails = { projects: [] };
            this.initDonutChart();
          }
        });
    } else {
      this.loadingPersonDetails = false;
      this.selectedPersonDetails = { projects: [] };
      this.initDonutChart();
    }
    
    modalRef.shown.subscribe(() => {
      if (!person?.projectTeamId) {
        setTimeout(() => {
          this.initDonutChart();
        }, 200);
      }
    });
  }
  
  initDonutChart(workload?: any): void {
    const canvas = document.getElementById('workingDetailsChart') as HTMLCanvasElement;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        const centerX = canvas.width / 2;
        const centerY = canvas.height / 2;
        const radius = Math.min(centerX, centerY) - 10;
        const innerRadius = radius * 0.6;
        
        const data = workload ? [
          { value: workload.critical || 0, color: '#dc3545' },
          { value: workload.warning || 0, color: '#ffc107' },
          { value: workload.moderate || 0, color: '#fd7e14' },
          { value: workload.normal || 0, color: '#28a745' }
        ] : [
          { value: 25, color: '#dc3545' }, 
          { value: 35, color: '#ffc107' },
          { value: 15, color: '#fd7e14' }, 
          { value: 25, color: '#28a745' } 
        ];
        
        let startAngle = 0;
   
        data.forEach(segment => {
          const endAngle = startAngle + (segment.value / 100) * Math.PI * 2;
          
          ctx.beginPath();
          ctx.arc(centerX, centerY, radius, startAngle, endAngle);
          ctx.arc(centerX, centerY, innerRadius, endAngle, startAngle, true);
          ctx.closePath();
          
          ctx.fillStyle = segment.color;
          ctx.fill();
          
          startAngle = endAngle;
        });
        
     
        ctx.beginPath();
        ctx.arc(centerX, centerY, innerRadius - 1, 0, Math.PI * 2);
        ctx.fillStyle = '#FFFFFF';
        ctx.fill();
      }
    }
  }

  openMoodSelector(index: number): void {
    document.removeEventListener('click', this.closeMoodSelector.bind(this));
    
    if (this.selectedPersonIndex === index) {
      this.selectedPersonIndex = undefined;
    } else {
      this.selectedPersonIndex = index;
      
     
      setTimeout(() => {    
        const boundCloseHandler = this.handleDocumentClick.bind(this);
        (this as any).currentClickHandler = boundCloseHandler;
        document.addEventListener('click', boundCloseHandler);
      }, 0);
    }
  }
  
  handleDocumentClick(event: Event): void {
    const target = event.target as Element;
    const moodButton = target.closest('.btn-lg');
    const moodSelector = target.closest('.mood-selector');
    if (moodButton && moodButton.contains(target) || moodSelector) {
      return;
    }
    
    this.closeMoodSelector();
  }
  
  closeMoodSelector(): void {
    if ((this as any).currentClickHandler) {
      document.removeEventListener('click', (this as any).currentClickHandler);
      (this as any).currentClickHandler = null;
    }
    
    this.selectedPersonIndex = undefined;
  }

  selectMood(mood: string, event?: Event): void {
    if (event) {
      
      event.stopPropagation();
    }
    
    if (this.selectedPersonIndex !== undefined) {
      if (this.selectedPersonIndex === -1) {
        this.teamLeaderForm.patchValue({ mood });
      } else {
        const control = this.teamMembers.at(this.selectedPersonIndex);
        control.patchValue({ mood });
      }
      
      this.closeMoodSelector();
      
      this.calculateTotals();
    }
  }

  openTeamLeaderMoodSelector(): void {
    document.removeEventListener('click', this.closeMoodSelector.bind(this));
    
    if (this.selectedPersonIndex === -1) {
      this.selectedPersonIndex = undefined;
    } else {
      this.selectedPersonIndex = -1;
      
      setTimeout(() => {
        const boundCloseHandler = this.handleDocumentClick.bind(this);
        (this as any).currentClickHandler = boundCloseHandler;
        document.addEventListener('click', boundCloseHandler);
      }, 0);
    }
  }

  selectTeamLeaderMood(mood: string, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    
    this.teamLeaderForm.patchValue({ mood });
    
    this.closeMoodSelector();
    
    this.calculateTotals();
  }

  onDragStart(event: DragEvent, staff: any): void {
    if (event.dataTransfer) {
      event.dataTransfer.setData('text', JSON.stringify(staff));
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
  }


  isStaffMemberAlreadyInTeam(employeeId: number, fullName?: string): boolean {
    const currentLeaderEmployeeId = this.teamLeaderForm.get('employeeId')?.value;
    const currentLeaderName = this.teamLeaderForm.get('name')?.value;
    
    if (currentLeaderEmployeeId === employeeId || 
        (fullName && currentLeaderName && currentLeaderName.trim() === fullName.trim())) {
      return true;
    }
    
    for (let i = 0; i < this.teamMembers.length; i++) {
      const member = this.teamMembers.at(i);
      const memberEmployeeId = member.get('employeeId')?.value;
      const memberName = member.get('name')?.value;
      
      if (memberEmployeeId === employeeId || 
          (fullName && memberName && memberName.trim() === fullName.trim())) {
        return true;
      }
    }
    
    return false;
  }

  getTeamConflictType(employeeId: number, fullName?: string): 'team-leader' | 'team-member' | 'none' {
    const currentLeaderEmployeeId = this.teamLeaderForm.get('employeeId')?.value;
    const currentLeaderName = this.teamLeaderForm.get('name')?.value;
    
    if (currentLeaderEmployeeId === employeeId || 
        (fullName && currentLeaderName && currentLeaderName.trim() === fullName.trim())) {
      return 'team-leader';
    }
    
    for (let i = 0; i < this.teamMembers.length; i++) {
      const member = this.teamMembers.at(i);
      const memberEmployeeId = member.get('employeeId')?.value;
      const memberName = member.get('name')?.value;
      
      if (memberEmployeeId === employeeId || 
          (fullName && memberName && memberName.trim() === fullName.trim())) {
        return 'team-member';
      }
    }
    
    return 'none';
  }

  onDrop(event: DragEvent, isTeamLeader: boolean = false): void {
    event.preventDefault();
    if (!this.canEditAssignment) return;
    if (event.dataTransfer) {
      const staffData = JSON.parse(event.dataTransfer.getData('text'));
      
      if (!staffData?.employee?.employeeId) {
        this.alertNotificationService.error('Invalid employee data.');
        return;
      }
      
      const employeeId = staffData.employee.employeeId;
      const fullName = `${staffData.employee.firstName} ${staffData.employee.lastName}`;
      
      const conflictType = this.getTeamConflictType(employeeId, fullName);
      
      if (conflictType !== 'none') {
        let message = '';
        if (conflictType === 'team-leader') {
          message = `${fullName} is already set as Team Leader. Cannot be added as ${isTeamLeader ? 'duplicate Team Leader' : 'Other Team Member'}.`;
        } else if (conflictType === 'team-member') {
          message = `${fullName} is already present among Other Team Members. ${isTeamLeader ? 'Cannot be promoted to Team Leader without being removed first.' : 'Cannot be added again.'}`;
        }
        this.alertNotificationService.warning(message);
        return;
      }
      
      if (isTeamLeader) {
        const currentLeaderName = this.teamLeaderForm.get('name')?.value;
        if (currentLeaderName && currentLeaderName.trim()) {
          this.confirmationDialogService.open(
            `Do you want to replace the current Team Leader (${currentLeaderName}) with ${fullName}?`,
            () => this.assignTeamLeader(staffData, employeeId, fullName)
          );
          return;
        }

        this.assignTeamLeader(staffData, employeeId, fullName);
      } else {
        const mood = staffData.actitude ? this.convertRateToMood(staffData.actitude) : 'neutral';

        this.addTeamMember({
          name: fullName,
          hours: 0,
          mood: mood,
          employeeId: employeeId,
          projectTeamId: null
        });

        this.alertNotificationService.success(`${fullName} has been added to the team.`);

        this.calculateTotals();
      }
    }
  }

  private assignTeamLeader(staffData: any, employeeId: number, fullName: string): void {
    const mood = staffData.actitude ? this.convertRateToMood(staffData.actitude) : 'neutral';

    this.teamLeaderForm.patchValue({
      name: fullName,
      hours: 0,
      mood: mood,
      employeeId: employeeId,
      projectTeamId: null
    });

    this.alertNotificationService.success(`${fullName} has been assigned as Team Leader.`);

    this.calculateTotals();
  }

  onDragMemberStart(event: DragEvent, memberData: any, index: number): void {
    if (event.dataTransfer) {
      memberData.memberIndex = index;
      memberData.projectTeamId = memberData.projectTeamId;
      event.dataTransfer.setData('text', JSON.stringify(memberData));
      event.dataTransfer.setData('memberType', 'teamMember');
    }
  }

  onDragTeamLeaderStart(event: DragEvent, leaderData: any): void {
    if (event.dataTransfer) {
      leaderData.projectTeamId = leaderData.projectTeamId;
      event.dataTransfer.setData('text', JSON.stringify(leaderData));
      event.dataTransfer.setData('memberType', 'teamLeader');
    }
  }
  
  onDropBackToAvailableStaff(event: DragEvent): void {
    event.preventDefault();
    if (!this.canEditAssignment) return;
    if (event.dataTransfer) {
      const dataType = event.dataTransfer.getData('memberType');
      const memberData = JSON.parse(event.dataTransfer.getData('text'));
      
      if (dataType === 'teamMember') {
        const index = memberData.memberIndex;
        const projectTeamId = memberData.projectTeamId;
        
        this.memberToDelete = memberData;
        this.teamMemberIndexToDelete = index;
        this.memberToDeleteProjectTeamId = projectTeamId;
        this.confirmationDialogService.open(
          `Are you sure you want to remove <strong>${memberData.name}</strong> from the team?`,
          () => this.onConfirmDelete()
        );
      } else if (dataType === 'teamLeader') {
        const projectTeamId = memberData.projectTeamId;
        
        this.memberToDelete = memberData;
        this.teamMemberIndexToDelete = -1;
        this.memberToDeleteProjectTeamId = projectTeamId;
        this.confirmationDialogService.open(
          `Are you sure you want to remove the team leader <strong>${memberData.name}</strong>?`,
          () => this.onConfirmDelete()
        );
      }
    }
  }

  validateTeamBeforeSubmit(): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];
    const formData = this.form.value;
    
    const memberEmployeeIds = new Set<number>();
    const memberNames = new Set<string>();
    
    formData.teamMembers.forEach((member: any, index: number) => {
      if (member?.employeeId && member?.name) {
        const employeeId = member.employeeId;
        const name = member.name.trim();
        
        if (memberEmployeeIds.has(employeeId)) {
          errors.push(`Duplicate Employee ID found in Other Team Members: ${name} (ID: ${employeeId})`);
        } else {
          memberEmployeeIds.add(employeeId);
        }
        if (memberNames.has(name.toLowerCase())) {
          errors.push(`Duplicate name found in Other Team Members: ${name}`);
        } else {
          memberNames.add(name.toLowerCase());
        }
        if (formData.teamLeader?.employeeId === employeeId) {
          errors.push(`${name} cannot be both Team Leader and Other Team Member`);
        }
        
        if (formData.teamLeader?.name && 
            formData.teamLeader.name.trim().toLowerCase() === name.toLowerCase()) {
          errors.push(`${name} appears duplicated between Team Leader and Other Team Members`);
        }
      }
    });
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }

  async submitForm(): Promise<void> {
    if (!this.projectId) {
      this.alertNotificationService.error('Invalid project ID');
      return;
    }
    
    const validation = this.validateTeamBeforeSubmit();
    if (!validation.isValid) {
      const errorMessage = 'Team validation errors:\n' + validation.errors.join('\n');
      this.alertNotificationService.error(errorMessage);
      return;
    }

    const teamDiffersFromBackend = this.buildSignatureFromForm() !== this.persistedTeamSignature;

    const formData = this.form.value;
    const apiCalls: (() => Observable<any>)[] = [];
    if (formData.teamLeader && formData.teamLeader.name) {
      const teamLeaderData = {
        projectTime: Number(formData.teamLeader.hours),
        role: 'TEAM_LEADER',
        rate: this.convertMoodToRate(formData.teamLeader.mood),
        employeeId: formData.teamLeader.employeeId
      };

      if (formData.teamLeader.projectTeamId) {
        apiCalls.push(() => this.projectsService.updateProjectTeams(
          Number(this.projectId),
          formData.teamLeader.projectTeamId,
          teamLeaderData
        ));
      } else {
        apiCalls.push(() => this.projectsService.createProjectTeams(teamLeaderData, Number(this.projectId)));
      }
    }


    formData.teamMembers.forEach((member: any) => {
      if (member && member.name) {
        const memberData = {
          projectTime: Number(member.hours),
          role: 'OTHER',
          rate: this.convertMoodToRate(member.mood),
          employeeId: member.employeeId
        };

        if (member.projectTeamId) {
          apiCalls.push(() => this.projectsService.updateProjectTeams(
            Number(this.projectId),
            member.projectTeamId,
            memberData
          ));
        } else {
          apiCalls.push(() => this.projectsService.createProjectTeams(memberData, Number(this.projectId)));
        }
      }
    });

    if (apiCalls.length === 0) {
      this.alertNotificationService.warning('No team members to save');
      return;
    }

    this.loading = true;
    const successes: any[] = [];
    const failures: any[] = [];

    for (const call of apiCalls) {
      try {
        successes.push(await firstValueFrom(call()));
      } catch (error) {
        failures.push(error);
      }
    }

    this.loading = false;

    this.loadTeamData();

    if (teamDiffersFromBackend && successes.length > 0) {
      this.loadTeamConflicts();
    }

    if (failures.length === 0) {
      this.alertNotificationService.success('Team updated successfully');
      this.onTeamUpdated.emit(successes);
    } else if (successes.length > 0) {
      this.alertNotificationService.warning(
        `Team partially saved: ${successes.length} of ${apiCalls.length} changes saved, ${failures.length} failed. Please review and retry.`
      );
      this.onTeamUpdated.emit(successes);
    } else {
      const firstError: any = failures[0];
      this.alertNotificationService.error('Error saving team: ' + (firstError?.message || 'Unknown error'));
    }
  }
  
  getValidationMessage(controlName: string, formType: 'leader' | 'member' = 'member', memberIndex?: number): string {
    let control: any;
    
    if (formType === 'leader') {
      control = this.teamLeaderForm.get(controlName);
    } else if (typeof memberIndex === 'number') {
      control = this.teamMembersArray.at(memberIndex)?.get(controlName);
    }
    
    if (control?.errors?.['duplicate']) {
      if (controlName === 'employeeId') {
        return 'This employee is already present in the team';
      } else if (controlName === 'name') {
        return 'This name is already present in the team';
      }
    }
    
    return '';
  }

  hasValidationError(controlName: string, formType: 'leader' | 'member' = 'member', memberIndex?: number): boolean {
    let control: any;
    
    if (formType === 'leader') {
      control = this.teamLeaderForm.get(controlName);
    } else if (typeof memberIndex === 'number') {
      control = this.teamMembersArray.at(memberIndex)?.get(controlName);
    }
    
    return control?.invalid && control?.errors;
  }

  getTeamDuplicateStats(): { 
    totalMembers: number; 
    duplicateEmployeeIds: number; 
    duplicateNames: number;
    hasLeader: boolean;
  } {
    const employeeIds = new Set<number>();
    const names = new Set<string>();
    let duplicateEmployeeIds = 0;
    let duplicateNames = 0;
    
    const leaderEmployeeId = this.teamLeaderForm.get('employeeId')?.value;
    const leaderName = this.teamLeaderForm.get('name')?.value;
    const hasLeader = !!(leaderEmployeeId && leaderName?.trim());
    
    if (hasLeader) {
      employeeIds.add(leaderEmployeeId);
      names.add(leaderName.trim().toLowerCase());
    }
    
    for (let i = 0; i < this.teamMembersArray.length; i++) {
      const member = this.teamMembersArray.at(i);
      const memberEmployeeId = member.get('employeeId')?.value;
      const memberName = member.get('name')?.value;
      
      if (memberEmployeeId) {
        if (employeeIds.has(memberEmployeeId)) {
          duplicateEmployeeIds++;
        } else {
          employeeIds.add(memberEmployeeId);
        }
      }
      
      if (memberName?.trim()) {
        const normalizedName = memberName.trim().toLowerCase();
        if (names.has(normalizedName)) {
          duplicateNames++;
        } else {
          names.add(normalizedName);
        }
      }
    }
    
    return {
      totalMembers: this.teamMembersArray.length + (hasLeader ? 1 : 0),
      duplicateEmployeeIds,
      duplicateNames,
      hasLeader
    };
  }

  ngOnDestroy(): void {
    if ((this as any).currentClickHandler) {
      document.removeEventListener('click', (this as any).currentClickHandler);
      (this as any).currentClickHandler = null;
    }
  }

  private updateHasOtherMembers(): void {
    this.hasOtherMembers = this.teamMembersArray.length > 0;
    this.revalidateAllTeamMembers();
  }

  private revalidateAllTeamMembers(): void {
    const leaderEmployeeId = this.teamLeaderForm.get('employeeId')?.value;
    const leaderName = this.teamLeaderForm.get('name')?.value;
    
    if (leaderEmployeeId) {
      this.validateTeamLeaderEmployeeId(leaderEmployeeId);
    }
    if (leaderName && leaderName.trim()) {
      this.validateTeamLeaderName(leaderName.trim());
    }
    
    for (let i = 0; i < this.teamMembersArray.length; i++) {
      const memberForm = this.teamMembersArray.at(i);
      const memberEmployeeId = memberForm.get('employeeId')?.value;
      const memberName = memberForm.get('name')?.value;
      
      if (memberEmployeeId) {
        this.validateMemberEmployeeId(memberForm, memberEmployeeId);
      }
      if (memberName && memberName.trim()) {
        this.validateMemberName(memberForm, memberName.trim());
      }
    }
  }

  addPlaceholderMember(): void {
    this.addTeamMember();
  }

  onConfirmDelete(): void {
    this.isDeleting = true;
    
    if (this.memberToDeleteProjectTeamId) {
      this.projectsService.deleteProjectTeams(Number(this.projectId), this.memberToDeleteProjectTeamId)
        .pipe(
          catchError(error => {
            this.alertNotificationService.error('Error deleting team member: ' + error.message);
            return of(null);
          }),
          finalize(() => {
            this.isDeleting = false;
            this.closeConfirmationDialog();
          })
        )
        .subscribe({
          next: (response) => {
            if (response) {
              this.alertNotificationService.success('Team member removed successfully');
              this.removeMemberFromForm();
              this.loadAvailableStaff(); 
              this.onTeamUpdated.emit(response.data);
            }
          }
        });
    } else {
      this.removeMemberFromForm();
      this.loadAvailableStaff();
      this.isDeleting = false;
      this.closeConfirmationDialog();
    }
  }

  onCancelDelete(): void {
    this.closeConfirmationDialog();
  }

  private removeMemberFromForm(): void {
    if (this.teamMemberIndexToDelete >= 0) {
      this.teamMembersArray.removeAt(this.teamMemberIndexToDelete);
    } else {
      this.teamLeaderForm.patchValue({
        name: '',
        hours: '',
        mood: 'neutral',
        projectTeamId: null,
        employeeId: null
      });
    }
    
    this.calculateTotals();
  }

  private closeConfirmationDialog(): void {
    this.memberToDelete = null;
    this.teamMemberIndexToDelete = -1;
    this.memberToDeleteProjectTeamId = undefined;
  }

}
